import { useState, useRef, useCallback } from 'react';
import { Mic, Send, Trash2 } from 'lucide-react';

// Gravador de voz do aplicativo do cliente.
//
// Proprio, e nao o `GravadorAudio` do /atendimento, pelo mesmo motivo que o
// catalogo publico nao reusa os componentes do ERP: alvo de toque de celular
// (44px+), sem `useDialog` (o dialogo da casa e' desenhado pra desktop com Esc) e
// erro dito em portugues de cliente, nao de operador. A logica de MediaRecorder e'
// curta o bastante pra que compartilha-la custasse mais do que repeti-la.
//
// ⚠️ O `mimeType` sai daqui e VIAJA junto do audio ate o disco (o servidor grava a
// extensao a partir dele). Um fallback silencioso pra formato que o servidor nao
// conhece viraria arquivo `.bin` que a vendedora nao consegue tocar — por isso os
// dois candidatos sao webm, e nao "o que o navegador quiser".
const MIME_PREFERIDO = 'audio/webm;codecs=opus';
const MIME_FALLBACK = 'audio/webm';

interface Props {
  onGravado: (blob: Blob, mime: string) => void;
  desabilitado?: boolean;
}

export function GravadorVoz({ onGravado, desabilitado }: Props) {
  const [gravando, setGravando] = useState(false);
  const [segundos, setSegundos] = useState(0);
  const [erro, setErro] = useState<string | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const canceladoRef = useRef(false);

  const iniciar = useCallback(async () => {
    setErro(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported(MIME_PREFERIDO) ? MIME_PREFERIDO : MIME_FALLBACK;
      const recorder = new MediaRecorder(stream, { mimeType });
      recorderRef.current = recorder;
      chunksRef.current = [];

      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      recorder.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        if (canceladoRef.current) { chunksRef.current = []; return; }
        onGravado(new Blob(chunksRef.current, { type: mimeType }), mimeType);
      };

      recorder.start();
      canceladoRef.current = false;
      setGravando(true);
      setSegundos(0);
      timerRef.current = setInterval(() => setSegundos((s) => s + 1), 1000);
    } catch (err) {
      const nome = (err as Error).name;
      setErro(
        nome === 'NotAllowedError' || nome === 'PermissionDeniedError'
          ? 'Toque no cadeado na barra de endereço e libere o microfone para gravar.'
          : nome === 'NotFoundError'
            ? 'Não encontramos um microfone neste aparelho.'
            : 'Não deu para acessar o microfone. Tente de novo.',
      );
    }
  }, [onGravado]);

  const encerrar = useCallback((cancelar: boolean) => {
    canceladoRef.current = cancelar;
    const r = recorderRef.current;
    if (r && r.state !== 'inactive') r.stop();
    if (timerRef.current) clearInterval(timerRef.current);
    setGravando(false);
  }, []);

  const fmt = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  if (gravando) {
    return (
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => encerrar(true)}
          aria-label="Descartar gravação"
          style={{ width: 44, height: 44 }}
          className="flex items-center justify-center rounded-full text-gray-500"
        >
          <Trash2 size={20} />
        </button>
        <button
          type="button"
          onClick={() => encerrar(false)}
          aria-label="Enviar áudio"
          style={{ height: 44, backgroundColor: '#dc2626', color: '#fff', paddingInline: 16 }}
          className="flex items-center gap-2 rounded-full font-semibold"
        >
          <span style={{ width: 8, height: 8, borderRadius: 999, backgroundColor: '#fff' }} />
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(segundos)}</span>
          <Send size={16} />
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center">
      <button
        type="button"
        onClick={iniciar}
        disabled={desabilitado}
        aria-label="Gravar áudio"
        style={{ width: 44, height: 44, opacity: desabilitado ? 0.4 : 1 }}
        className="flex items-center justify-center rounded-full text-gray-600"
      >
        <Mic size={22} />
      </button>
      {erro && (
        <span style={{ position: 'absolute', bottom: 60, left: 12, right: 12, fontSize: 13, color: '#b91c1c', backgroundColor: '#fef2f2', padding: '8px 12px', borderRadius: 8 }}>
          {erro}
        </span>
      )}
    </div>
  );
}
